import {
  PhoneNumberType,
  PhoneNumberFormat,
  PhoneNumberUtil,
} from 'google-libphonenumber'
import slug from 'slug'
import hash from 'object-hash'
import cloneDeep from 'clone-deep'
import Color from 'color'

slug.defaults.mode = 'rfc3986'

export function clone(value) {
  try {
    return structuredClone(value)
  } catch (e) {
    return cloneDeep(value)
  }
}

export { v4 as uuidv4, validate as isValidUUID } from 'uuid'
export { isValid as validateCpf } from '@fnando/cpf'
export { default as omit } from 'lodash.omit'
export { deepEqual as equal } from 'fast-equals'
export { debounce } from 'debounce'
export { default as deepForEach } from 'deep-for-each'
export { slug, hash }

export function fullTrim(str) {
  str = str ?? ''

  return str.replace(/(?:(?:^|\n)\s+|\s+(?:$|\n))/g, '').replace(/\s+/g, ' ')
}

export function sanitizeFullName(fullName) {
  fullName = fullTrim(fullName)

  // Capitalize
  fullName = fullName.replace(/(^|\s)\S/g, l => l.toUpperCase())

  return fullName
}

export function getPhoneUtil() {
  return PhoneNumberUtil.getInstance()
}

export function getPhoneNumber(phone, region = 'BR') {
  try {
    if (typeof phone !== 'string') return phone

    const phoneUtil = getPhoneUtil()

    return phoneUtil.parse(phone, region)
  } catch (e) {
    return phone
  }
}

export function getPhoneNumberType(phone, region = 'BR') {
  const phoneUtil = getPhoneUtil()
  const phoneNumber = getPhoneNumber(phone, region)

  return phoneUtil.getNumberType(phoneNumber)
}

export function sanitizePhoneNumber(phoneNumber) {
  try {
    if (!phoneNumber) return phoneNumber

    const phoneUtil = getPhoneUtil()

    phoneNumber = getPhoneNumber(phoneNumber)

    return phoneUtil.format(phoneNumber, PhoneNumberFormat.E164)
  } catch (e) {
    return phoneNumber
  }
}

export function isMobilePhoneNumber(phone) {
  phone = sanitizePhoneNumber(phone)
  // console.log('phone[6]', phone[5])
  if (phone.length === 13 && phone[5] >= 6 && phone[5] <= 9)
    phone = phone.slice(0, 5).concat(9).concat(phone.slice(5))

  return getPhoneNumberType(phone) === PhoneNumberType.MOBILE
}

export function isPossiblePhoneNumber(phoneNumber) {
  const phoneUtil = getPhoneUtil()

  phoneNumber = getPhoneNumber(phoneNumber)

  return phoneUtil.isPossibleNumber(phoneNumber)
}

export function isValidPhoneNumber(phoneNumber) {
  const phoneUtil = getPhoneUtil()

  phoneNumber = getPhoneNumber(phoneNumber)

  return phoneUtil.isValidNumber(phoneNumber)
}

export function getFormattedDateTime(
  dateTime,
  {
    hour = '2-digit',
    minute = '2-digit',
    day = 'numeric',
    month = 'numeric',
    year = 'numeric',
    dateTimeSeparator = ' às ',
    ...otherOptions
  } = {},
) {
  let formattedDateTime = new Date(dateTime).toLocaleString(undefined, {
    hour,
    minute,
    day,
    month,
    year,
    ...otherOptions,
  })

  if (dateTimeSeparator)
    formattedDateTime = formattedDateTime.replace(/\s/g, dateTimeSeparator)

  return formattedDateTime
}

export function validatePhoneNumber(
  phoneNumber,
  { requireMobilePhone = true } = {},
) {
  try {
    phoneNumber = sanitizePhoneNumber(phoneNumber)

    if (phoneNumber.length === 0) return 'EMPTY_PHONE_NUMBER'

    const isMobilePhoneNumberResult = isMobilePhoneNumber(phoneNumber)

    if (requireMobilePhone === true && !isMobilePhoneNumberResult)
      return 'IS_NOT_MOBILE_NUMBER'

    if (isMobilePhoneNumberResult && phoneNumber.length <= 13)
      return 'NINTH_DIGIT_NOT_SET'

    if (!isPossiblePhoneNumber(phoneNumber) || !isValidPhoneNumber(phoneNumber))
      return 'INVALID_PHONE_NUMBER'

    return true
  } catch (e) {
    console.error(e)
    return 'INVALID_PHONE_NUMBER'
  }
}

export function getFormattedPhoneNumber(phoneNumber, { region = 'BR' } = {}) {
  let formattedPhoneNumber = phoneNumber

  try {
    const phoneUtil = getPhoneUtil()

    if (!phoneNumber) return

    phoneNumber = getPhoneNumber(phoneNumber, region)

    formattedPhoneNumber = phoneUtil.format(
      phoneNumber,
      PhoneNumberFormat.NATIONAL,
    )

    if (formattedPhoneNumber.length === 15)
      formattedPhoneNumber = formattedPhoneNumber.replace(' 9', ' 9 ')
    else if (isMobilePhoneNumber(formattedPhoneNumber)) {
      if (formattedPhoneNumber.length === 14)
        formattedPhoneNumber = formattedPhoneNumber.replace(' ', ' 9 ')
    }
  } finally {
    return formattedPhoneNumber
  }
}

export function validateFullName(fullName) {
  fullName = sanitizeFullName(fullName)

  if (fullName.length === 0) return 'EMPTY_FULL_NAME'

  const INVALID_FULL_NAME = 'INVALID_FULL_NAME'

  if (fullName.includes(' ') === false) return 'COMPOUND_NAME_NOT_PRESENT'

  const fullNameWords = fullName.split(' ')
  const fullNameLastWord = fullNameWords[fullNameWords.length - 1]

  if (fullNameLastWord.length <= 1) return INVALID_FULL_NAME

  const fullNameWordsWithOneChar = fullNameWords.filter(
    word => word.replace(/\./g, '').length === 1,
  )
  const enabledOneChars = ['E', 'O', 'D', 'A']
  const hasWordsWithConsonant = fullNameWordsWithOneChar.some(
    word => !enabledOneChars.includes(word),
  )

  if (hasWordsWithConsonant) return INVALID_FULL_NAME

  return true
}

export function isValidEmail(email) {
  return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email,
  )
}

export function validateEmail(email) {
  if (!email) return 'EMPTY_EMAIL'
  if (isValidEmail(email) === false) return 'INVALID_EMAIL'

  return true
}

export function getSetting(settings = [], key) {
  return settings.find(setting => setting.key === key)
}

export function getSettingValue(settings = [], key) {
  return getSetting(settings, key)?.value
}

export function accentsTidy(value) {
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

export function sanitizePaymentCardHolderName(holderName = '') {
  holderName = holderName || ''
  holderName = fullTrim(holderName)
  holderName = slug(holderName, {
    replacement: ' ',
  })
  holderName = holderName.toUpperCase()

  return holderName
}

export function validatePaymentCardNumber(paymentCardNumber) {
  if (!paymentCardNumber) return 'EMPTY_NUMBER'

  const { number } = require('card-validator')
  const { isValid } = number(paymentCardNumber)

  return isValid
}

export function validatePaymentCardExpiry(expiry) {
  const { month, year } = getPaymentCardExpiry(expiry)

  if (!month && !year) return 'EMPTY'

  if (!month) return 'MONTH_NOT_DEFINED'

  if (!year) return 'YEAR_NOT_DEFINED'

  if (month < 1 || month > 12) return 'INVALID_MONTH'

  const now = new Date()
  const currentYear = now.getFullYear()

  if (year < currentYear) return 'INVALID_YEAR'

  const currentMonth = now.getMonth() + 1

  if (year === currentYear && month < currentMonth) return 'INVALID_EXPIRY'

  return true
}

export function getPaymentCardExpiry(expiry) {
  let month, year

  switch (typeof expiry) {
    case 'object':
      month = expiry?.month
      year = expiry?.year
      break

    case 'string':
      const split = (expiry ?? '').toString().split('/', 2)

      month = split?.[0]
      year = split?.[1]
      break
  }

  if (month) month = month.toString().trim().padStart(2, '0')

  if (year) {
    const currentYear = new Date().getFullYear().toString()
    const firstNumbersCurrentYear = currentYear.substring(0, 2)

    year = Number.parseInt(
      year
        .toString()
        .trim()
        .padStart(currentYear.length, firstNumbersCurrentYear),
    )
  }

  return {
    month,
    year,
  }
}

export function getPaymentCardExpiryFormatted(expiry) {
  let { month, year } = getPaymentCardExpiry(expiry)

  if (!month || !year) return

  month = (month ?? '').toString().trim().padStart(2, '0')

  return `${month}/${year}`
}

export function getPaymentCardBrandName({ number }) {
  const CardValidator = require('card-validator')

  return CardValidator.number(number)?.card?.niceType
}

export function getPaymentCardBrandImage({ number }) {
  const paymentCardBrandName = getPaymentCardBrandName({ number })
  const cardBrandSlug = slug(paymentCardBrandName)

  return getPaymentFormImage(cardBrandSlug)
}

export function getPaymentCardNumberLastDigits({ number }) {
  number = number.toString()

  return number.substr(number.length - 4)
}

export function getPaymentCardId({ number, holderName, expiry }) {
  const normalizedPaymentCard = {
    number,
    expiry: getPaymentCardExpiry(expiry),
    holderName,
  }

  return hash(normalizedPaymentCard)
}

export function getOnlyNumbers(value) {
  return (value ?? '').toString().replace(/[^0-9]/g, '')
}

export function validateZipCode(zipCode) {
  zipCode = getOnlyNumbers(zipCode)

  return zipCode.length === 8
}

export function getFormattedCurrency(value, { currency = 'R$' } = {}) {
  const numberFormat = require('locutus/php/strings/number_format')

  return `${currency} ${numberFormat(value, 2, ',', '.')}`
}

export function hasOnlyNumbers(value) {
  return /^[0-9]+$/.test(value.toString())
}

export function validatePaymentCardSecurityCode(securityCode) {
  if (!securityCode) return 'EMPTY_SECURITY_CODE'

  securityCode = securityCode.toString()

  if (securityCode.length < 3) return 'INVALID_LENGTH_SECURITY_CODE'

  if (hasOnlyNumbers(securityCode) === false) return 'INVALID_SECURITY_CODE'

  return true
}

export function defaultFilterFunction(item) {
  if (item?.isVisible === false) return false

  if (item?.startAt || item?.endAt) {
    const now = new Date()

    if (item?.startAt) {
      const startAt = new Date(item.startAt)

      if (now < startAt) return false
    }

    if (item?.endAt) {
      const endAt = new Date(item.endAt)

      if (now > endAt) return false
    }
  }

  const activeSchedules = (item.schedules ?? []).filter(
    ({ active }) => active === true,
  )

  if (activeSchedules.length > 0) {
    const currentWeekday = new Date().getDay()
    const schedule = activeSchedules.find(
      ({ weekday }) => weekday === currentWeekday,
    )

    if (!schedule) return false

    const now = new Date()
    const currentDate = now.toISOString().split('T', 2)[0]
    const startTime = schedule.startTime
      ? new Date(`${currentDate}T${schedule.startTime}-03:00`)
      : schedule.startTime
    const endTime = schedule.endTime
      ? new Date(`${currentDate}T${schedule.endTime}-03:00`)
      : schedule.endTime

    if ((startTime && now < startTime) || (endTime && now > endTime))
      return false
  }

  return true
}

export function defaultSortFunction(i1, i2, callback) {
  if (typeof callback === 'function') return callback(i1, i2)

  if (i1?.active === false && i2?.active === true) return 1
  if (i1?.active === true && i2?.active === false) return -1

  const i1OrderNum = i1?.orderNum ?? Number.POSITIVE_INFINITY
  const i2OrderNum = i2?.orderNum ?? Number.POSITIVE_INFINITY

  if (i1OrderNum > i2OrderNum) return 1
  if (i1OrderNum < i2OrderNum) return -1

  return 0
}

export function isUrl(url) {
  try {
    return Boolean(new URL(url))
  } catch (e) {
    return false
  }
}

export function getPaymentFormImage(paymentForm) {
  let paymentFormImage =
    typeof paymentForm === 'string' ? paymentForm : paymentForm?.image

  if (!paymentFormImage) paymentFormImage = slug(paymentForm?.name)
  else if (isUrl(paymentFormImage)) return paymentFormImage

  if (paymentFormImage.indexOf('.') === -1)
    paymentFormImage = `${paymentFormImage}.svg`

  const { ASSETS_DOMAIN } = require('@/constants')

  paymentFormImage = `https://${ASSETS_DOMAIN}/images/payment-forms/${paymentFormImage}`

  return paymentFormImage
}

export function calculate(value, operation) {
  if (operation === undefined) return value
  if (typeof operation !== 'string') operation = operation?.toString?.()

  let result = value
  let operator
  let operationValue = operation

  const hasPercentage = operation.endsWith('%')
  const firstChar = operation.charAt(0)

  if (['-', '+', '*', '/', '='].includes(firstChar)) {
    operator = firstChar
    operationValue = operation.slice(1)
  }

  operationValue = operationValue.replace(/%/g, '')
  operationValue = Number(operationValue)

  if (hasPercentage === true) operationValue = (operationValue * value) / 100

  switch (operator) {
    case '+':
      result += operationValue
      break

    case '-':
      result -= operationValue
      break

    case '*':
      result *= operationValue
      break

    case '/':
      result /= operationValue
      break

    default:
      result = operationValue
      break
  }

  return result
}

export function getProductImages({ images = [] }) {
  return images.map(image => image?.thumbnail?.url ?? image)
}

export function getOrderItemImages({ images, products = [] }) {
  return images ?? products.flatMap(getProductImages)
}

export async function importScript(
  src,
  { crossOrigin, defer, async = true } = {},
) {
  return new Promise((resolve, reject) => {
    const scriptElement = document.createElement('script')

    scriptElement.src = src
    scriptElement.async = async
    scriptElement.onload = resolve
    scriptElement.onerror = reject

    if (crossOrigin) scriptElement.crossOrigin = crossOrigin

    if (defer !== undefined) scriptElement.defer = defer

    document.body.appendChild(scriptElement)
  })
}

export async function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function naturalJoin(
  value,
  { separator = ', ', conjunction = ' e ' } = {},
) {
  let string = ''

  if (Array.isArray(value)) {
    const count = value.length

    if (count === 1) string = value[0]
    else if (count > 1) {
      const list = value.slice(0, -1)
      const last = value[count - 1]

      string = `${list.join(separator)}${conjunction}${last}`
    }
  }

  return string
}
export function isEmptyObject(object) {
  try {
    return Object.keys(object).length === 0
  } catch (e) {
    return false
  }
}

export function updateObject(currentObject, newObject) {
  for (const n in newObject)
    if (currentObject.hasOwnProperty(n) === false) delete currentObject[n]

  for (const n in newObject) currentObject[n] = newObject[n]

  return newObject
}

export function getArrayRandomValue(array) {
  return array[Math.floor(Math.random() * array.length)]
}

export function getTextColor(color) {
  let textColor = '#000'

  if (Color(color).luminosity() <= 0.4) textColor = '#fff'

  return textColor
}

export async function getSupportWhatsAppURL(cityId) {
  const { settings = [] } = await app.callApi({
    method: 'GET',
    version: 1,
    action: `cities/${cityId}`,
  })
  const setting = settings.find(({ key }) => key === 'whatsappUrl')

  return setting?.value
}

export function formatSeconds(totalSeconds) {
  totalSeconds = Math.round(totalSeconds)

  let minutes = Math.floor(totalSeconds / 60)
  let seconds = totalSeconds % 60

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0',
  )}`
}

export function mergeObjects(object1, object2) {
  const result = {}

  function copyProperties(source, target) {
    Object.getOwnPropertyNames(source).forEach(prop => {
      const propertyDescriptor = Object.getOwnPropertyDescriptor(source, prop)
      Object.defineProperty(target, prop, propertyDescriptor)
    })
  }

  copyProperties(object1, result)
  copyProperties(object2, result)

  return result
}

export function hashObject(object, ignoreProps = ['hash']) {
  let clonedObject = clone(object)

  ignoreProps.forEach(prop => delete clonedObject[prop])

  return hash(clonedObject)
}

export function formatPercentage(value) {
  if (typeof value === 'number') value /= 100

  return new Intl.NumberFormat(undefined, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
}

export function fixFloat(value, precision = 2) {
  return Number(value.toFixed(precision))
}

export function getValue(
  value,
  { acceptTypes = ['number', 'string'], dictionary },
) {
  const selfFunction = getValue
  const {
    companySectionPortionId,
    companySectionSizeId,
    numberProducts,
    numberUniqueProducts,
  } = dictionary

  let nextValue

  nextValue = value?.sizes?.[companySectionSizeId]

  if (companySectionSizeId !== undefined && nextValue !== undefined)
    return selfFunction(nextValue, arguments[1])

  nextValue = value?.portions?.[companySectionPortionId]

  if (companySectionPortionId !== undefined && nextValue !== undefined)
    return selfFunction(nextValue, arguments[1])

  nextValue = value?.numberProducts?.[numberProducts]

  if (numberProducts !== undefined && nextValue !== undefined)
    return selfFunction(nextValue, arguments[1])

  nextValue = value?.numberUniqueProducts?.[numberUniqueProducts]

  if (numberUniqueProducts !== undefined && nextValue !== undefined)
    return selfFunction(nextValue, arguments[1])

  if ([null, undefined].includes(value) || acceptTypes.includes(typeof value))
    return value
}

export function extractValues(obj) {
  let values = []

  for (let key in obj) {
    const value = obj[key]

    if (typeof value === 'object') {
      values = values.concat(extractValues(value))
    } else {
      values.push(value)
    }
  }

  return values
}
